import React from 'react'
import MatchScreenTracking from '../components/Tracking/MatchScreenTracking'
import { isFramed } from '../utils/isFramed'
import { MODULE_KEYS } from './moduleRegistry'
import { HeadingModule } from './modules/HeadingModule/HeadingModule'
import { withModuleGuard } from './hocs/withModuleGuard'
import { TrustSignalsModule } from './modules/TrustSignalsModule/TrustSignalsModule'
import { ConciergeModule } from './modules/ConciergeModule/ConciergeModule'
import { MatchTabsModule } from './modules/MatchTabsModule/MatchTabsModule'
import { DisclosureModule } from './modules/DisclosureModule/DisclosureModule'
import { FavCalculatorModule } from './modules/FavCalculatorModule/FavCalculatorModule'
import { AdsModule } from './modules/AdsModule/AdsModule'
import { CsatModule } from './modules/CsatModule/CsatModule'
import { ArticleCarouselModule } from './modules/ArticleCarouselModule/ArticleCarouselModule'
import { ExpandedFeedbackModule } from './modules/ExpandedFeedbackModule/ExpandedFeedbackModule'

/**
 * MODULES is the single place to register redesign modules.
 *
 * Each entry is a component wrapped with `withModuleGuard`, which reads
 * `window.matchScreenRedesign.variables` at render time and returns null if
 * the corresponding Optimizely key is absent. This means:
 *
 * - Presence of a key in `variables` is the feature gate. Optimizely controls
 *   which modules appear by including or omitting their key — no per-module
 *   conditional logic is needed here.
 * - Render order matches declaration order. Reorder this array to reorder
 *   the page layout.
 * - Adding a new module is one line: `withModuleGuard(MyModule, MODULE_KEYS.myModule)`.
 *   No other file needs to change.
 *
 * Guarded components are created at module-initialisation time (not inside
 * render) so React sees stable component references across re-renders.
 */
const MODULES = [
  withModuleGuard(HeadingModule, MODULE_KEYS.heading),
  withModuleGuard(TrustSignalsModule, MODULE_KEYS.trustSignals),
  withModuleGuard(ConciergeModule, MODULE_KEYS.conciergeBanner),
  withModuleGuard(MatchTabsModule, MODULE_KEYS.matchTabs),
  withModuleGuard(DisclosureModule, MODULE_KEYS.disclosure),
  withModuleGuard(FavCalculatorModule, MODULE_KEYS.favCalculator),
  withModuleGuard(AdsModule, MODULE_KEYS.ads),
  withModuleGuard(ArticleCarouselModule, MODULE_KEYS.articleCarousel),
  withModuleGuard(CsatModule, MODULE_KEYS.csat),
  withModuleGuard(ExpandedFeedbackModule, MODULE_KEYS.feedback)
]

/**
 * Root of the redesigned match-screen, mounted when
 * `window.matchScreenRedesign.enabled === true`. Placeholder shell — follow-up
 * stories will compose new module components here.
 *
 * `MatchScreenTracking` mounts unconditionally so page-load and advisor-impression
 *  events fire identically across both variations. Relocate it out
 * of `../components/` when sunsetting legacy so that folder can be deleted.
 */
const MatchScreenAppRedesigned = () => {
  // Embedded contexts drop the site-header padding and tighten the module gap.
  // isFramed() is the one place that decides this.
  const rootClassName = isFramed()
    ? 'match-screen-app match-screen-app--redesign match-screen-app--framed'
    : 'match-screen-app match-screen-app--redesign'

  return (
    <div
      className={rootClassName}
      data-testid="match-screen-redesign-root"
    >
      <MatchScreenTracking />
      {MODULES.map((Module, i) => <Module key={i} />)}
    </div>
  )
}

export default MatchScreenAppRedesigned
